import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { empresaService, usuarioService, usuarioEmpresaService, departamentoService } from '@/services/api';
import { Empresa, Usuario, Departamento } from '@/types';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ArrowLeft,
  Building2,
  Users,
  FolderTree,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Shield,
  Mail,
  User,
  Power,
  Target,
  FolderKanban,
  FileSpreadsheet,
  ShieldCheck,
  CalendarRange,
  Network,
  CalendarDays,
  GraduationCap,
  Save,
  Check,
  ChevronsUpDown,
  Eye,
  EyeOff,
  Handshake,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { DadosEmpresaFields, emptyDadosEmpresa, dadosEmpresaFromEmpresa, type DadosEmpresaValues } from '@/components/empresa/DadosEmpresaFields';

const tipoLabels: Record<string, string> = {
  superadmin: 'Super Admin',
  admin: 'Administrador',
  comum: 'Usuário',
};

interface ModuleDef {
  key: string;
  label: string;
  description: string;
  icon: string;
}

const MODULE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  okrs: Target,
  projetos: FolderKanban,
  relatorios: FileSpreadsheet,
  auditoria: ShieldCheck,
  cronogramas: CalendarRange,
  organograma: Network,
  calendario: CalendarDays,
  treinamentos: GraduationCap,
  crm: Handshake,
};

export default function GerenciarEmpresa() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [empresa, setEmpresa] = useState<Empresa | null>(null);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [availableModules, setAvailableModules] = useState<ModuleDef[]>([]);
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [isSavingModules, setIsSavingModules] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Dialog states
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [isDeptDialogOpen, setIsDeptDialogOpen] = useState(false);
  const [isDeleteUserDialogOpen, setIsDeleteUserDialogOpen] = useState(false);
  const [isDeleteDeptDialogOpen, setIsDeleteDeptDialogOpen] = useState(false);
  const [isEditEmpresaDialogOpen, setIsEditEmpresaDialogOpen] = useState(false);
  const [isToggleEmpresaDialogOpen, setIsToggleEmpresaDialogOpen] = useState(false);
  const [isDeleteEmpresaDialogOpen, setIsDeleteEmpresaDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<Usuario | null>(null);
  const [selectedDept, setSelectedDept] = useState<Departamento | null>(null);

  // User form
  const [userForm, setUserForm] = useState({
    nome: '',
    email: '',
    tipo: 'comum' as 'admin' | 'comum',
    departamento_id: '',
    senha: '',
  });
  const [userFormInitial, setUserFormInitial] = useState<typeof userForm>({
    nome: '',
    email: '',
    tipo: 'comum',
    departamento_id: '',
    senha: '',
  });

  // Vincular usuário existente
  const [userDialogMode, setUserDialogMode] = useState<'novo' | 'vincular'>('novo');
  const [existingUserSearch, setExistingUserSearch] = useState('');
  const [existingUserResults, setExistingUserResults] = useState<Usuario[]>([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const [selectedExistingUser, setSelectedExistingUser] = useState<Usuario | null>(null);
  const [existingUserPopoverOpen, setExistingUserPopoverOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Dept form
  const [deptForm, setDeptForm] = useState({ nome: '' });
  const [deptFormInitial, setDeptFormInitial] = useState({ nome: '' });

  // Empresa form
  const [empresaForm, setEmpresaForm] = useState({ nome: '' });
  const [empresaFormInitial, setEmpresaFormInitial] = useState({ nome: '' });

  // Dados cadastrais da empresa (CNPJ, razão social, endereço)
  const [dadosEmpresa, setDadosEmpresa] = useState<DadosEmpresaValues>(emptyDadosEmpresa);
  const [dadosEmpresaInitial, setDadosEmpresaInitial] = useState<DadosEmpresaValues>(emptyDadosEmpresa);
  const [isSavingDados, setIsSavingDados] = useState(false);
  const hasDadosChanges = JSON.stringify(dadosEmpresa) !== JSON.stringify(dadosEmpresaInitial);

  const loadData = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const [empresaRes, usuariosRes, deptRes, modulesRes] = await Promise.all([
        empresaService.getById(id),
        usuarioService.getByEmpresa(id),
        departamentoService.getByEmpresa(id),
        empresaService.getAvailableModules(),
      ]);
      if (empresaRes.success) {
        setEmpresa(empresaRes.data);
        setSelectedModules(empresaRes.data.modulos_habilitados || []);
        const dados = dadosEmpresaFromEmpresa(empresaRes.data);
        setDadosEmpresa(dados);
        setDadosEmpresaInitial(dados);
      }
      if (usuariosRes.success) setUsuarios(usuariosRes.data);
      if (deptRes.success) setDepartamentos(deptRes.data);
      if (modulesRes.success) setAvailableModules(modulesRes.data);
    } catch {
      toast.error('Erro ao carregar dados');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (user?.tipo !== 'superadmin') return;
    loadData();
  }, [user, loadData]);

  const openNewUser = () => {
    const initial = { nome: '', email: '', tipo: 'comum' as const, departamento_id: '', senha: '' };
    setUserForm(initial);
    setUserFormInitial(initial);
    setSelectedUser(null);
    setUserDialogMode('novo');
    setExistingUserSearch('');
    setExistingUserResults([]);
    setSelectedExistingUser(null);
    setShowPassword(false);
    setIsUserDialogOpen(true);
  };

  const openEditUser = (usuario: Usuario) => {
    setSelectedUser(usuario);
    const initial = {
      nome: usuario.nome,
      email: usuario.email,
      tipo: usuario.tipo as 'admin' | 'comum',
      departamento_id: usuario.departamento_id || '',
      senha: '',
    };
    setUserForm(initial);
    setUserFormInitial(initial);
    setShowPassword(false);
    setIsUserDialogOpen(true);
  };

  const handleCloseUserDialog = useCallback(() => {
    setIsUserDialogOpen(false);
    setSelectedUser(null);
    setUserForm({ nome: '', email: '', tipo: 'comum', departamento_id: '', senha: '' });
    setUserFormInitial({ nome: '', email: '', tipo: 'comum', departamento_id: '', senha: '' });
    setUserDialogMode('novo');
    setExistingUserSearch('');
    setExistingUserResults([]);
    setSelectedExistingUser(null);
    setExistingUserPopoverOpen(false);
    setShowPassword(false);
  }, []);

  useEffect(() => {
    if (!isUserDialogOpen || userDialogMode !== 'vincular') return;
    const timer = setTimeout(async () => {
      setIsSearchingUsers(true);
      try {
        const result = await usuarioService.getPaginated({ page: 1, limit: 20, search: existingUserSearch });
        if (result.success) {
          const jaVinculados = new Set(usuarios.map((u) => u.id));
          setExistingUserResults(result.data.filter((u) => !jaVinculados.has(u.id)));
        }
      } finally {
        setIsSearchingUsers(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [existingUserSearch, isUserDialogOpen, userDialogMode, usuarios]);

  const handleSaveUser = async () => {
    if (!empresa) return;
    try {
      if (selectedUser) {
        const result = await usuarioService.update(selectedUser.id, {
          nome: userForm.nome,
          email: userForm.email,
          tipo: userForm.tipo,
          empresa_id: empresa.id,
          departamento_id: userForm.departamento_id || null,
          ...(userForm.senha ? { senha: userForm.senha } : {}),
        });
        if (result.success) {
          toast.success('Usuário atualizado');
          loadData();
          handleCloseUserDialog();
        } else {
          toast.error(result.error || 'Erro ao atualizar usuário');
        }
      } else if (userDialogMode === 'vincular') {
        if (!selectedExistingUser) {
          toast.error('Selecione um usuário');
          return;
        }
        const result = await usuarioEmpresaService.create({
          usuario_id: selectedExistingUser.id,
          empresa_id: empresa.id,
          tipo: userForm.tipo,
          departamento_id: userForm.departamento_id || null,
        });
        if (result.success) {
          toast.success('Usuário vinculado à empresa');
          loadData();
          handleCloseUserDialog();
        } else {
          toast.error(result.error || 'Erro ao vincular usuário');
        }
      } else {
        if (!userForm.senha) {
          toast.error('Senha é obrigatória');
          return;
        }
        const result = await usuarioService.create({
          nome: userForm.nome,
          email: userForm.email,
          senha: userForm.senha,
          tipo: userForm.tipo,
          empresa_id: empresa.id,
          departamento_id: userForm.departamento_id || null,
        });
        if (result.success) {
          toast.success('Usuário criado');
          loadData();
          handleCloseUserDialog();
        } else {
          toast.error(result.error || 'Erro ao criar usuário');
        }
      }
    } catch {
      toast.error('Erro ao salvar usuário');
    }
  };

  const openDeleteUser = (usuario: Usuario) => {
    setSelectedUser(usuario);
    setIsDeleteUserDialogOpen(true);
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    try {
      const result = await usuarioService.delete(selectedUser.id);
      if (result.success) {
        toast.success('Usuário excluído');
        loadData();
        setIsDeleteUserDialogOpen(false);
        setSelectedUser(null);
      } else {
        toast.error(result.error || 'Erro ao excluir usuário');
      }
    } catch {
      toast.error('Erro ao excluir usuário');
    }
  };

  const openNewDept = () => {
    setDeptForm({ nome: '' });
    setDeptFormInitial({ nome: '' });
    setSelectedDept(null);
    setIsDeptDialogOpen(true);
  };

  const openEditDept = (dept: Departamento) => {
    setSelectedDept(dept);
    setDeptForm({ nome: dept.nome });
    setDeptFormInitial({ nome: dept.nome });
    setIsDeptDialogOpen(true);
  };

  const handleCloseDeptDialog = useCallback(() => {
    setIsDeptDialogOpen(false);
    setSelectedDept(null);
    setDeptForm({ nome: '' });
    setDeptFormInitial({ nome: '' });
  }, []);

  const handleSaveDept = async () => {
    if (!empresa || !deptForm.nome.trim()) return;
    try {
      if (selectedDept) {
        const result = await departamentoService.update(selectedDept.id, { nome: deptForm.nome });
        if (result.success) {
          toast.success('Departamento atualizado');
          loadData();
          handleCloseDeptDialog();
        } else {
          toast.error(result.error || 'Erro ao atualizar departamento');
        }
      } else {
        const result = await departamentoService.create({ nome: deptForm.nome, empresa_id: empresa.id });
        if (result.success) {
          toast.success('Departamento criado');
          loadData();
          handleCloseDeptDialog();
        } else {
          toast.error(result.error || 'Erro ao criar departamento');
        }
      }
    } catch {
      toast.error('Erro ao salvar departamento');
    }
  };

  const openDeleteDept = (dept: Departamento) => {
    setSelectedDept(dept);
    setIsDeleteDeptDialogOpen(true);
  };

  const handleDeleteDept = async () => {
    if (!selectedDept) return;
    try {
      const result = await departamentoService.delete(selectedDept.id);
      if (result.success) {
        toast.success('Departamento excluído');
        loadData();
        setIsDeleteDeptDialogOpen(false);
        setSelectedDept(null);
      } else {
        toast.error(result.error || 'Erro ao excluir departamento');
      }
    } catch {
      toast.error('Erro ao excluir departamento');
    }
  };

  const openEditEmpresa = () => {
    if (!empresa) return;
    setEmpresaForm({ nome: empresa.nome });
    setEmpresaFormInitial({ nome: empresa.nome });
    setIsEditEmpresaDialogOpen(true);
  };

  const handleCloseEditEmpresaDialog = useCallback(() => {
    setIsEditEmpresaDialogOpen(false);
    setEmpresaForm({ nome: '' });
    setEmpresaFormInitial({ nome: '' });
  }, []);

  const handleUpdateEmpresa = async () => {
    if (!empresa || !empresaForm.nome.trim()) return;
    try {
      const result = await empresaService.update(empresa.id, { nome: empresaForm.nome });
      if (result.success) {
        toast.success('Empresa atualizada');
        loadData();
        handleCloseEditEmpresaDialog();
      } else {
        toast.error(result.error || 'Erro ao atualizar empresa');
      }
    } catch {
      toast.error('Erro ao atualizar empresa');
    }
  };

  const handleToggleStatus = async () => {
    if (!empresa) return;
    try {
      const result = await empresaService.toggleStatus(empresa.id);
      if (result.success) {
        toast.success(`Empresa ${result.data?.status === 'ativa' ? 'ativada' : 'desativada'}`);
        loadData();
        setIsToggleEmpresaDialogOpen(false);
      } else {
        toast.error(result.error || 'Erro ao alterar status');
      }
    } catch {
      toast.error('Erro ao alterar status');
    }
  };

  const handleDeleteEmpresa = async () => {
    if (!empresa) return;
    try {
      const result = await empresaService.delete(empresa.id);
      if (result.success) {
        toast.success('Empresa excluída com sucesso');
        navigate('/empresas');
      } else {
        toast.error(result.error || 'Erro ao excluir empresa');
      }
    } catch {
      toast.error('Erro ao excluir empresa');
    } finally {
      setIsDeleteEmpresaDialogOpen(false);
    }
  };

  const handleSaveModules = async () => {
    if (!empresa || selectedModules.length === 0) {
      toast.error('Pelo menos um módulo deve estar habilitado');
      return;
    }
    setIsSavingModules(true);
    try {
      const result = await empresaService.update(empresa.id, { modulos_habilitados: selectedModules });
      if (result.success) {
        toast.success('Módulos atualizados');
        loadData();
      } else {
        toast.error(result.error || 'Erro ao atualizar módulos');
      }
    } catch {
      toast.error('Erro ao atualizar módulos');
    } finally {
      setIsSavingModules(false);
    }
  };

  const handleSaveDados = async () => {
    if (!empresa) return;
    setIsSavingDados(true);
    try {
      const result = await empresaService.update(empresa.id, dadosEmpresa);
      if (result.success) {
        toast.success('Dados da empresa atualizados');
        loadData();
      } else {
        toast.error(result.error || 'Erro ao atualizar dados da empresa');
      }
    } catch {
      toast.error('Erro ao atualizar dados da empresa');
    } finally {
      setIsSavingDados(false);
    }
  };

  if (user?.tipo !== 'superadmin') {
    return <Navigate to="/dashboard" replace />;
  }

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  if (!empresa) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <p className="text-muted-foreground">Empresa não encontrada</p>
          <Button onClick={() => navigate('/empresas')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar para Empresas
          </Button>
        </div>
      </AppLayout>
    );
  }

  const getDeptUserCount = (deptId: string) =>
    usuarios.filter(u => u.departamento_id === deptId).length;

  return (
    <AppLayout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/empresas')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-muted-foreground" />
                <h1 className="text-xl font-bold tracking-tight">{empresa.nome}</h1>
              </div>
              <div className="flex items-center gap-3 mt-1">
                <Badge
                  variant={empresa.status === 'ativa' ? 'default' : 'secondary'}
                  className={cn(
                    'text-xs',
                    empresa.status === 'ativa'
                      ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100'
                      : 'bg-gray-100 text-gray-600'
                  )}
                >
                  {empresa.status === 'ativa' ? 'Ativa' : 'Inativa'}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {new Date(empresa.data_criacao).toLocaleDateString('pt-BR')}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={openEditEmpresa}>
              <Pencil className="h-4 w-4 mr-1" />
              Editar
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsToggleEmpresaDialogOpen(true)}
              className={cn(
                empresa.status === 'ativa'
                  ? 'text-destructive hover:text-destructive'
                  : 'text-emerald-600 hover:text-emerald-700'
              )}
            >
              <Power className="h-4 w-4 mr-1" />
              {empresa.status === 'ativa' ? 'Desativar' : 'Ativar'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsDeleteEmpresaDialogOpen(true)}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Excluir
            </Button>
          </div>
        </div>

        {/* Cards stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 px-4">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Total Usuários</span>
              </div>
              <p className="text-2xl font-bold mt-1">{usuarios.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 px-4">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-blue-500" />
                <span className="text-sm text-muted-foreground">Administradores</span>
              </div>
              <p className="text-2xl font-bold mt-1">{usuarios.filter(u => u.tipo === 'admin').length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 px-4">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Usuários Comuns</span>
              </div>
              <p className="text-2xl font-bold mt-1">{usuarios.filter(u => u.tipo === 'comum').length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 px-4">
              <div className="flex items-center gap-2">
                <FolderTree className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Departamentos</span>
              </div>
              <p className="text-2xl font-bold mt-1">{departamentos.length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Módulos habilitados */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4" />
                  Módulos Habilitados
                </CardTitle>
                <CardDescription>Selecione quais módulos esta empresa pode acessar</CardDescription>
              </div>
              <Button size="sm" onClick={handleSaveModules} disabled={isSavingModules || selectedModules.length === 0}>
                <Save className="h-4 w-4 mr-1" />
                {isSavingModules ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {availableModules.map((mod) => {
                const IconComp = MODULE_ICONS[mod.key] || FolderKanban;
                const isChecked = selectedModules.includes(mod.key);
                return (
                  <div
                    key={mod.key}
                    className={cn(
                      'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all',
                      isChecked
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-muted-foreground/30'
                    )}
                    onClick={() => {
                      setSelectedModules(prev =>
                        prev.includes(mod.key)
                          ? prev.filter(m => m !== mod.key)
                          : [...prev, mod.key]
                      );
                    }}
                  >
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={() => {
                        setSelectedModules(prev =>
                          prev.includes(mod.key)
                            ? prev.filter(m => m !== mod.key)
                            : [...prev, mod.key]
                        );
                      }}
                      className="mt-0.5"
                    />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <IconComp className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-sm">{mod.label}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{mod.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Dados cadastrais da empresa */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Dados da Empresa
                </CardTitle>
                <CardDescription>CNPJ, razão social e endereço (opcionais). Preencha o CEP para autocompletar.</CardDescription>
              </div>
              <Button size="sm" onClick={handleSaveDados} disabled={isSavingDados || !hasDadosChanges}>
                <Save className="h-4 w-4 mr-1" />
                {isSavingDados ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <DadosEmpresaFields
              values={dadosEmpresa}
              onChange={(patch) => setDadosEmpresa((prev) => ({ ...prev, ...patch }))}
              idPrefix="gerenciar-empresa"
            />
          </CardContent>
        </Card>

        {/* Two column layout: Users + Departments */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Users */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Usuários
                  </CardTitle>
                  <CardDescription>{usuarios.length} cadastrado(s)</CardDescription>
                </div>
                <Button size="sm" onClick={openNewUser}>
                  <Plus className="h-4 w-4 mr-1" />
                  Novo
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {usuarios.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <Users className="h-12 w-12 mb-3 opacity-50" />
                  <p className="text-sm">Nenhum usuário cadastrado</p>
                  <Button variant="link" size="sm" onClick={openNewUser} className="mt-2">
                    Criar primeiro usuário
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {usuarios.map((usuario) => (
                    <div
                      key={usuario.id}
                      className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <User className="h-4 w-4 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{usuario.nome}</p>
                          <p className="text-xs text-muted-foreground truncate">{usuario.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-12 sm:ml-0">
                        <Badge
                          variant="outline"
                          className={cn(
                            'text-xs shrink-0',
                            usuario.tipo === 'admin' && 'bg-blue-100 text-blue-800 border-blue-200',
                            usuario.tipo === 'comum' && 'bg-gray-100 text-gray-800 border-gray-200'
                          )}
                        >
                          {tipoLabels[usuario.tipo]}
                        </Badge>
                        {usuario.departamento_id && (
                          <Badge variant="secondary" className="text-xs shrink-0">
                            {departamentos.find(d => d.id === usuario.departamento_id)?.nome || 'Depto'}
                          </Badge>
                        )}
                        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => openEditUser(usuario)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive shrink-0" onClick={() => openDeleteUser(usuario)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Departments */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <FolderTree className="h-4 w-4" />
                    Departamentos
                  </CardTitle>
                  <CardDescription>{departamentos.length} cadastrado(s)</CardDescription>
                </div>
                <Button size="sm" onClick={openNewDept}>
                  <Plus className="h-4 w-4 mr-1" />
                  Novo
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {departamentos.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <FolderTree className="h-12 w-12 mb-3 opacity-50" />
                  <p className="text-sm">Nenhum departamento cadastrado</p>
                  <Button variant="link" size="sm" onClick={openNewDept} className="mt-2">
                    Criar primeiro departamento
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {departamentos.map((dept) => {
                    const deptUserCount = getDeptUserCount(dept.id);
                    return (
                      <div
                        key={dept.id}
                        className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <FolderTree className="h-4 w-4 text-primary" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{dept.nome}</p>
                            <p className="text-xs text-muted-foreground">
                              {deptUserCount} usuário{deptUserCount !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditDept(dept)}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => openDeleteDept(dept)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* User Dialog */}
      <Dialog open={isUserDialogOpen} onOpenChange={(open) => { if (!open) handleCloseUserDialog(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedUser ? 'Editar Usuário' : 'Novo Usuário'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!selectedUser && (
              <Tabs value={userDialogMode} onValueChange={(v) => setUserDialogMode(v as 'novo' | 'vincular')}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="novo">Criar novo</TabsTrigger>
                  <TabsTrigger value="vincular">Vincular existente</TabsTrigger>
                </TabsList>
              </Tabs>
            )}
            {!selectedUser && userDialogMode === 'vincular' ? (
              <div className="space-y-2">
                <Label htmlFor="user_existente">Usuário</Label>
                <Popover open={existingUserPopoverOpen} onOpenChange={setExistingUserPopoverOpen}>
                  <PopoverTrigger asChild>
                    <button
                      id="user_existente"
                      type="button"
                      className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring"
                    >
                      <span className={selectedExistingUser ? '' : 'text-muted-foreground'}>
                        {selectedExistingUser ? `${selectedExistingUser.nome} (${selectedExistingUser.email})` : 'Buscar usuário por nome ou email'}
                      </span>
                      <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder="Buscar usuário..."
                        value={existingUserSearch}
                        onValueChange={setExistingUserSearch}
                      />
                      <CommandList>
                        {isSearchingUsers ? (
                          <div className="py-4 text-center text-sm text-muted-foreground">Buscando...</div>
                        ) : (
                          <>
                            <CommandEmpty>Nenhum usuário encontrado.</CommandEmpty>
                            <CommandGroup>
                              {existingUserResults.map((u) => (
                                <CommandItem
                                  key={u.id}
                                  value={u.id}
                                  onSelect={() => {
                                    setSelectedExistingUser(u);
                                    setExistingUserPopoverOpen(false);
                                  }}
                                >
                                  <Check className={cn('mr-2 h-4 w-4', selectedExistingUser?.id === u.id ? 'opacity-100' : 'opacity-0')} />
                                  <div className="flex flex-col">
                                    <span>{u.nome}</span>
                                    <span className="text-xs text-muted-foreground">{u.email}</span>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </>
                        )}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="user_nome">Nome</Label>
                  <Input
                    id="user_nome"
                    value={userForm.nome}
                    onChange={(e) => setUserForm({ ...userForm, nome: e.target.value })}
                    placeholder="Nome completo"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="user_email">Email</Label>
                  <Input
                    id="user_email"
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                    placeholder="email@empresa.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="user_senha">
                    {selectedUser ? 'Nova Senha (deixe em branco para manter)' : 'Senha'}
                  </Label>
                  <div className="relative">
                    <Input
                      id="user_senha"
                      type={showPassword ? 'text' : 'password'}
                      value={userForm.senha}
                      onChange={(e) => setUserForm({ ...userForm, senha: e.target.value })}
                      placeholder="••••••"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label htmlFor="user_tipo">Tipo</Label>
              <Select
                value={userForm.tipo}
                onValueChange={(value) => setUserForm({ ...userForm, tipo: value as 'admin' | 'comum' })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="comum">Usuário</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="user_depto">Departamento</Label>
              <Select
                value={userForm.departamento_id || 'none'}
                onValueChange={(value) => setUserForm({ ...userForm, departamento_id: value === 'none' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o departamento" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum</SelectItem>
                  {departamentos.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseUserDialog}>Cancelar</Button>
            <Button
              onClick={handleSaveUser}
              disabled={
                !selectedUser && userDialogMode === 'vincular'
                  ? !selectedExistingUser
                  : !userForm.nome || !userForm.email || (!selectedUser && !userForm.senha)
              }
            >
              {selectedUser ? 'Salvar' : userDialogMode === 'vincular' ? 'Vincular Usuário' : 'Criar Usuário'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dept Dialog */}
      <Dialog open={isDeptDialogOpen} onOpenChange={(open) => { if (!open) handleCloseDeptDialog(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedDept ? 'Editar Departamento' : 'Novo Departamento'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="dept_nome">Nome</Label>
              <Input
                id="dept_nome"
                value={deptForm.nome}
                onChange={(e) => setDeptForm({ nome: e.target.value })}
                placeholder="Ex: Tecnologia"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDeptDialog}>Cancelar</Button>
            <Button onClick={handleSaveDept} disabled={!deptForm.nome.trim()}>
              {selectedDept ? 'Salvar' : 'Criar Departamento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Empresa Dialog */}
      <Dialog open={isEditEmpresaDialogOpen} onOpenChange={(open) => { if (!open) handleCloseEditEmpresaDialog(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Empresa</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit_empresa_nome">Nome</Label>
              <Input
                id="edit_empresa_nome"
                value={empresaForm.nome}
                onChange={(e) => setEmpresaForm({ nome: e.target.value })}
                placeholder="Nome da empresa"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseEditEmpresaDialog}>Cancelar</Button>
            <Button onClick={handleUpdateEmpresa} disabled={!empresaForm.nome.trim()}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Toggle Empresa Confirmation */}
      <AlertDialog open={isToggleEmpresaDialogOpen} onOpenChange={setIsToggleEmpresaDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {empresa?.status === 'ativa' ? 'Desativar Empresa' : 'Ativar Empresa'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {empresa?.status === 'ativa'
                ? `Tem certeza que deseja desativar a empresa "${empresa?.nome}"? Os usuários não poderão mais acessar o sistema.`
                : `Deseja reativar a empresa "${empresa?.nome}"? Os usuários voltarão a ter acesso.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleToggleStatus}
              className={empresa?.status === 'ativa' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Empresa Confirmation */}
      <AlertDialog open={isDeleteEmpresaDialogOpen} onOpenChange={setIsDeleteEmpresaDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Empresa</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir a empresa <strong>{empresa?.nome}</strong>?
              Esta ação não pode ser desfeita e todos os dados relacionados serão removidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteEmpresa}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete User Confirmation */}
      <AlertDialog open={isDeleteUserDialogOpen} onOpenChange={setIsDeleteUserDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Usuário</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o usuário <strong>{selectedUser?.nome}</strong>?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsDeleteUserDialogOpen(false)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Dept Confirmation */}
      <AlertDialog open={isDeleteDeptDialogOpen} onOpenChange={setIsDeleteDeptDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Departamento</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o departamento <strong>{selectedDept?.nome}</strong>?
              {getDeptUserCount(selectedDept?.id || '') > 0 && (
                <> Existem {getDeptUserCount(selectedDept?.id || '')} usuário(s) vinculados a este departamento.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsDeleteDeptDialogOpen(false)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteDept} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
